import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import { encaissementMode } from '@lba-dev/package.local-globals/getDataById';
import { E_ENC } from '@lba-dev/package.local-globals/encaissementType';
import { Link } from '@mui/material';
import { CSVLink } from 'react-csv';
import { SaveAlt } from '@mui/icons-material';

import CustomMaterialTable from '../../../CustomMaterialTable';
import Money from '../../../../utils/Money';
import api from '../../../../api';

const PAGE_SIZE = 1000;

const parseItems = (enc) => {
  if (enc.multis && enc.multis.length > 0) {
    return enc.multis;
  }
  return [{ id: enc.id, montant: enc.montant, tva: enc.tva }];
};

const fetchEncaissements = async (filterStr, options = {}) => {
  const countRes = await api.encaissements.getAll({
    call: 'count',
    query: filterStr,
  });
  const totalCount = countRes.statusCode() === 200
    ? countRes.body().data()
    : 0;
  if (options.all) {
    const pages = Math.ceil(totalCount / PAGE_SIZE);
    const requests = Array.from({ length: pages }, (_, i) =>
      api.encaissements.getAll({
        query: filterStr,
        display: PAGE_SIZE,
        page: i,
      })
    );
    const results = await Promise.all(requests);
    const data = [];
    results.forEach((res) => {
      res.body().forEach((e) => {
        data.push(e.data());
      });
    });
    return data;
  }
  const dataRes = await api.encaissements.getAll({
    query: filterStr,
    display: options.pageSize,
    page: options.page,
  });
  const rows = [];
  dataRes.body().forEach((e, idx) => {
    const enc = e.data();
    parseItems(enc).forEach((item) => {
      const tva = item.tva || 10;
      const montantHT = item.montant / (1 + (tva / 100));
      rows.push({
        tableData: { id: `${enc._id}-${item.id}-${idx}` },
        interventionId: item.id,
        dateEncaissement: enc.encaissement,
        type: enc.type,
        mode: enc.mode,
        montantHT,
        tva,
      });
    });
  });
  return { data: rows, totalCount, page: options.page };
};

const formatCsvRows = (encs, type) => {
  const rows = [];
  encs.forEach((enc) => {
    parseItems(enc).forEach((item) => {
      const tva = item.tva || 10;
      const montantHT = item.montant / (1 + (tva / 100));
      rows.push({
        'ID Intervention': item.id,
        'Date encaissement': dayjs(enc.encaissement).format('DD/MM/YYYY'),
        ...(type === 'Solde'
          ? { 'Type': enc.type === E_ENC ? 'Encaissement' : 'Décaissement' }
          : {}),
        'Mode': encaissementMode[enc.mode]?.name || enc.mode,
        'Montant HT': Money.toString(montantHT, true),
        'TVA': `${tva}%`,
      });
    });
  });
  return rows;
};

const EncaissementListDialog = ({ filter, type }) => {
  const [rowPerPage, setRowPerPage] = useState(10);
  const [csvData, setCsvData] = useState([]);

  useEffect(() => {
    if (!filter) {
      return;
    }
    const loadCsvData = async () => {
      const data = await fetchEncaissements(
        JSON.stringify(filter), { all: true }
      );
      setCsvData(formatCsvRows(data, type));
    };
    loadCsvData();
  }, [filter, type]);

  const csvFilename =
    `encaissements_${dayjs().format('YYYY-MM-DD')}.csv`;


  const columns = [
    {
      title: 'ID Intervention',
      field: 'interventionId',
      render: (rowData) => (
        <Link
          component="button"
          onClick={() => window.open(
            `/intervention/${rowData.interventionId}`, '_blank')}
          style={{ cursor: 'pointer' }}
        >
          {rowData.interventionId}
        </Link>
      ),
    },
    {
      title: 'Date d\'encaissement',
      field: 'dateEncaissement',
      render: (rowData) => dayjs(rowData.dateEncaissement).format('DD/MM/YYYY'),
    },
    ...(type === 'Solde' ? [{
      title: 'Type',
      field: 'type',
      render: (rowData) => (rowData.type === E_ENC ?
        'Encaissement' : 'Décaissement'),
    }] : []),
    {
      title: 'Mode',
      field: 'mode',
      render: (rowData) => (
        encaissementMode[rowData.mode]?.name || rowData.mode
      ),
    },
    {
      title: 'Montant HT',
      field: 'montantHT',
      render: (rowData) => Money.toString(rowData.montantHT, true),
    },
    {
      title: 'TVA',
      field: 'tva',
      render: (rowData) => `${rowData.tva}%`,
    },
  ];
  let tableTitle = '';
  if (type === 'Solde') {
    tableTitle = 'Liste des Encaissements et Décaissements';
  } else if (type === 'Encaissements') {
    tableTitle = 'Liste des Encaissements';
  } else {
    tableTitle = 'Liste des Décaissements';
  }


  return (
    <CustomMaterialTable
      key={JSON.stringify(filter)}
      title={tableTitle}
      columns={columns}
      data={(query) => {
        if (!filter) {
          return Promise.resolve({ data: [], page: 0, totalCount: 0 });
        }
        return fetchEncaissements(JSON.stringify(filter), query);
      }}
      options={{
        search: true,
        paging: true,
        pageSize: rowPerPage,
        pageSizeOptions: [10, 20, 50],
        actionsColumnIndex: -1,
        onChangeRowsPerPage: (pageSize) => setRowPerPage(pageSize),
      }}
      actions={[
        {
          icon: () => (
            <CSVLink
              data={csvData}
              filename={csvFilename}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <SaveAlt />
            </CSVLink>
          ),
          tooltip: 'Exporter en CSV',
          isFreeAction: true,
        },
      ]}
    />
  );
};

EncaissementListDialog.propTypes = {
  filter: PropTypes.object,
  type: PropTypes.string,
};

EncaissementListDialog.defaultProps = {
  filter: null,
  type: 'encaissements',
};

const mapStateToProps = ({ dialog }) => ({
  filter: dialog.contentProps?.filter,
  type: dialog.contentProps?.type,
});

export default connect(mapStateToProps)(EncaissementListDialog);
